/**
 * AICharacter - REAL, independent Minecraft player entity for the AI.
 *
 * This wraps a second mineflayer bot instance (see aiBotConnection.js), NOT window.bot.
 * The AI has its own bot.entity (position/rotation), bot.inventory, bot.game, bot.health, etc.
 * All movement/mining/placing calls the real mineflayer API against that bot, which talks to
 * the real local flying-squid server over an independent in-memory channel. Nothing here is
 * simulated locally or teleported for its own sake — every action is a real protocol-level
 * change validated by the server, same as the human player's actions.
 */

import Vec3 from 'vec3';

const MAX_QUEUE_LENGTH = 50; // hard cap, avoids runaway/infinite action floods (req #10)
const MAX_MOVE_STEPS = 400;  // hard cap per single move action, avoids infinite walk loops

export class AICharacter {
  constructor(options = {}) {
    this.bot = null; // the AI's OWN mineflayer bot (never window.bot)
    this.mode = options.mode || 'creative'; // 'creative' or 'survival'
    this.actionQueue = [];
    this.isExecuting = false;
    this.cancelRequested = false;
    this.currentTask = null;
    this.inventory = {};
    this.enabled = false;
    this.lastActionTime = 0;
    this.actionDelayMs = 150; // throttle between queue steps
  }

  /**
   * @param {import('mineflayer').Bot} aiBot the AI's own bot, from aiBotConnection.connectAIBot()
   */
  async initialize(aiBot) {
    const playerBot = typeof window !== 'undefined' ? window.bot : undefined;
    if (!aiBot) {
      // Hard guard: this must never silently fall back to controlling the player's own bot.
      throw new Error('AICharacter.initialize() requires the AI\'s own bot instance, not window.bot');
    }
    this.bot = aiBot;
    this.enabled = true;
    this.trackInventory();
    await this.syncModeToServer();
    return this;
  }

  trackInventory() {
    if (!this.bot?.inventory) return;
    const updateCache = () => {
      this.inventory = {};
      for (const slot of this.bot.inventory.slots) {
        if (slot) this.inventory[slot.name] = (this.inventory[slot.name] || 0) + slot.count;
      }
    };
    this.bot.inventory.on('updateSlot', updateCache);
    updateCache();
  }

  async syncModeToServer() {
    if (!this.bot) return;
    const desired = this.mode === 'creative' ? 'creative' : 'survival';
    if (this.bot.game?.gameMode !== desired) {
      try {
        this.bot.chat(`/gamemode ${desired} ${this.bot.username}`);
      } catch {
        // Non-fatal: executor-level checks below do not depend on server-reported gameMode.
      }
    }
    if (this.mode === 'survival' && this.bot.creative) {
      this.bot.creative.stopFlying();
    }
  }

  setMode(mode) {
    if (!['creative', 'survival'].includes(mode)) {
      throw new Error(`Invalid mode: ${mode}. Must be 'creative' or 'survival'`);
    }
    this.mode = mode;
    void this.syncModeToServer();
  }

  // ---- Queue ----

  queueAction(action) {
    if (this.actionQueue.length >= MAX_QUEUE_LENGTH) {
      return { ok: false, error: `Action queue full (max ${MAX_QUEUE_LENGTH})` };
    }
    this.actionQueue.push(action);
    return { ok: true };
  }

  clearQueue() {
    this.actionQueue = [];
  }

  /**
   * Runs the queue sequentially: one action at a time, waiting for each result before
   * starting the next. Stops immediately on 'stop' action or cancellation (req #10).
   */
  async runQueue(onActionResult) {
    if (this.isExecuting) return { ok: false, error: 'Queue already running' };
    this.isExecuting = true;
    this.cancelRequested = false;

    const results = [];
    let steps = 0;
    while (this.actionQueue.length > 0 && !this.cancelRequested) {
      if (++steps > MAX_QUEUE_LENGTH) {
        results.push({ ok: false, error: 'Max queue steps exceeded, stopping' });
        break;
      }
      const action = this.actionQueue.shift();
      const result = await this.executeAction(action);
      results.push({ action, result });
      onActionResult?.(action, result);

      if (!result.ok && result.fatal) break;
      if (action.type === 'stop') break;
      await new Promise(r => setTimeout(r, this.actionDelayMs));
    }

    this.isExecuting = false;
    return { ok: true, results, cancelled: this.cancelRequested };
  }

  cancel() {
    this.cancelRequested = true;
    this.actionQueue = [];
    if (this.bot) {
      this.bot.clearControlStates();
    }
  }

  // ---- Action execution with hard, executor-level mode validation (req #8, #9) ----

  async executeAction(action) {
    if (!this.bot) return { ok: false, error: 'AI character not initialized', fatal: true };
    if (!action || typeof action.type !== 'string') {
      return { ok: false, error: 'Invalid action: missing type' };
    }

    // These checks do NOT trust the AI model or its prompt — re-checked here regardless of
    // what the model claims.
    if (action.type === 'fly' || action.fly) {
      if (this.mode !== 'creative') {
        return { ok: false, error: 'Survival mode cannot fly (blocked by action executor)' };
      }
    }
    if (action.type === 'teleport') {
      return { ok: false, error: 'Teleportation is not a permitted action in either mode' };
    }
    if (action.type === 'spawnItem' || action.type === 'giveItem') {
      return { ok: false, error: 'Direct item spawning is not a permitted action' };
    }

    try {
      switch (action.type) {
        case 'move': return await this.actionMove(action);
        case 'look': return await this.actionLook(action);
        case 'jump': return await this.actionJump();
        case 'breakBlock': return await this.actionBreakBlock(action);
        case 'placeBlock': return await this.actionPlaceBlock(action);
        case 'collectItem': return await this.actionCollectItem(action);
        case 'craft': return await this.actionCraft(action);
        case 'equip': return await this.actionEquip(action);
        case 'obtainCreativeItem': return await this.actionObtainCreative(action);
        case 'followPlayer': return await this.actionFollowPlayer(action);
        case 'stop': return this.actionStop();
        case 'chat': return this.actionChat(action);
        default:
          return { ok: false, error: `Unknown/unvalidated action type: ${action.type}` };
      }
    } catch (err) {
      return { ok: false, error: err.message };
    }
  }

  // ---- Movement ----
  // Real navigation using genuine mineflayer control-state + physics tick APIs (this fork does
  // not have mineflayer-pathfinder installed — see aiCharacter research notes). Walks the real
  // entity step by step, re-evaluating direction each tick; does basic auto-jump over a single
  // blocking block directly ahead. This is NOT full A* obstacle avoidance.

  async actionMove(action) {
    const { pos } = action;
    if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number' || typeof pos.z !== 'number') {
      return { ok: false, error: 'Invalid position' };
    }
    const target = new Vec3(pos.x, pos.y, pos.z);

    if (this.mode === 'creative' && action.fly) {
      await this.bot.creative.flyTo(target);
      return { ok: true, done: true, flew: true };
    }

    let steps = 0;
    while (this.bot.entity.position.distanceTo(target) > 0.6 && steps < MAX_MOVE_STEPS && !this.cancelRequested) {
      steps++;
      const delta = target.minus(this.bot.entity.position);
      const yaw = Math.atan2(-delta.x, -delta.z);
      await this.bot.look(yaw, 0, true);

      this.bot.setControlState('forward', true);
      const aheadBlock = this.bot.blockAt(this.bot.entity.position.offset(
        -Math.sin(yaw) * 0.5, 0, -Math.cos(yaw) * 0.5
      ));
      this.bot.setControlState('jump', !!(aheadBlock && aheadBlock.boundingBox === 'block'));

      await new Promise(r => setTimeout(r, 50));
    }

    this.bot.setControlState('forward', false);
    this.bot.setControlState('jump', false);

    const finalDistance = this.bot.entity.position.distanceTo(target);
    if (this.cancelRequested) return { ok: false, error: 'Cancelled' };
    if (steps >= MAX_MOVE_STEPS && finalDistance > 0.6) {
      return { ok: false, error: `Could not reach target after ${MAX_MOVE_STEPS} steps (likely obstructed — no full pathfinding available)`, distance: finalDistance };
    }
    return { ok: true, done: true, distance: finalDistance };
  }

  async actionLook(action) {
    const { yaw, pitch } = action;
    if (typeof yaw !== 'number' || typeof pitch !== 'number') {
      return { ok: false, error: 'Invalid yaw/pitch' };
    }
    await this.bot.look(yaw, pitch, false);
    return { ok: true };
  }

  async actionJump() {
    this.bot.setControlState('jump', true);
    await new Promise(r => setTimeout(r, 250));
    this.bot.setControlState('jump', false);
    return { ok: true };
  }

  async actionBreakBlock(action) {
    const { pos } = action;
    if (!pos) return { ok: false, error: 'No position provided' };

    const blockPos = new Vec3(pos.x, pos.y, pos.z);
    const block = this.bot.blockAt(blockPos);
    if (!block || block.name === 'air') {
      return { ok: false, error: 'Block not found or is air' };
    }
    const distance = this.bot.entity.position.distanceTo(blockPos);
    if (distance > 6) {
      return { ok: false, error: `Block too far away (${distance.toFixed(1)}), move closer first` };
    }

    if (this.mode === 'survival') {
      if (block.harvestTools && Object.keys(block.harvestTools).length > 0) {
        const heldId = this.bot.heldItem?.type;
        const canHarvest = heldId != null && !!block.harvestTools[heldId];
        if (!canHarvest) {
          return { ok: false, error: `Survival mode: need correct tool to harvest ${block.name}` };
        }
      }
    }

    await this.bot.dig(block);
    return { ok: true, blockName: block.name };
  }

  async actionPlaceBlock(action) {
    const { pos, blockName, face } = action;
    if (!pos || !blockName || !face) {
      return { ok: false, error: 'Missing pos, blockName, or face' };
    }

    let item = this.bot.inventory.slots.find(slot => slot && slot.name === blockName);
    if (!item) {
      if (this.mode === 'survival') {
        return { ok: false, error: `Survival mode: missing ${blockName} in inventory — gather or craft it first` };
      }
      const obtainResult = await this.actionObtainCreative({ itemName: blockName, count: 64 });
      if (!obtainResult.ok) return obtainResult;
      item = this.bot.inventory.slots.find(slot => slot && slot.name === blockName);
      if (!item) return { ok: false, error: `Failed to obtain ${blockName} even in creative mode` };
    }

    const referencePos = new Vec3(pos.x, pos.y, pos.z);
    const referenceBlock = this.bot.blockAt(referencePos);
    if (!referenceBlock) return { ok: false, error: 'Reference block not found (chunk not loaded?)' };

    const distance = this.bot.entity.position.distanceTo(referencePos);
    if (distance > 6) {
      return { ok: false, error: `Too far to place (${distance.toFixed(1)} blocks away)` };
    }

    const equipResult = await this.actionEquip({ itemName: blockName, hand: 'hand' });
    if (!equipResult.ok) return equipResult;

    try {
      await this.bot.placeBlock(referenceBlock, new Vec3(face.x, face.y, face.z));
      return { ok: true };
    } catch (err) {
      return { ok: false, error: `Placement rejected by world/server: ${err.message}` };
    }
  }

  async actionCollectItem(action) {
    const { itemName } = action;
    if (!itemName) return { ok: false, error: 'No item name provided' };

    const entities = Object.values(this.bot.entities).filter(
      e => e.name === 'item' && (!itemName || e.getDroppedItem?.()?.name?.includes(itemName))
    );
    if (entities.length === 0) {
      return { ok: false, error: `No ${itemName} item entities nearby` };
    }
    const closest = entities.sort((a, b) =>
      this.bot.entity.position.distanceTo(a.position) - this.bot.entity.position.distanceTo(b.position)
    )[0];

    return this.actionMove({ pos: { x: closest.position.x, y: closest.position.y, z: closest.position.z } });
  }

  async actionCraft(action) {
    const { itemName, count = 1 } = action;
    if (!itemName) return { ok: false, error: 'No item name provided' };

    const itemId = this.bot.registry.itemsByName[itemName]?.id;
    if (itemId == null) return { ok: false, error: `Unknown item: ${itemName}` };

    const recipe = this.bot.recipesFor(itemId, null, 1, null)?.[0];
    if (!recipe) {
      return { ok: false, error: `No known recipe for ${itemName} (may need a crafting table nearby)` };
    }
    try {
      // bot.craft() consumes real inventory ingredients and throws if insufficient — this is
      // the real survival enforcement, not a separate manual check.
      await this.bot.craft(recipe, count, null);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: `Craft failed: ${err.message}` };
    }
  }

  async actionEquip(action) {
    const { itemName, hand } = action;
    if (!itemName) return { ok: false, error: 'No item name provided' };
    const destination = hand === 'off-hand' ? 'off-hand' : 'hand';

    const item = this.bot.inventory.slots.find(slot => slot && slot.name === itemName);
    if (!item) return { ok: false, error: `${itemName} not in inventory, cannot equip` };

    try {
      await this.bot.equip(item, destination);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: `Equip failed: ${err.message}` };
    }
  }

  /**
   * Creative-only: obtains an item via the REAL creative-mode server mechanism
   * (bot.creative.setInventorySlot -> real 'set_creative_slot' packet, server-validated).
   */
  async actionObtainCreative(action) {
    if (this.mode !== 'creative') {
      return { ok: false, error: 'obtainCreativeItem is only permitted in creative mode' };
    }
    const { itemName, count = 64 } = action;
    if (!itemName) return { ok: false, error: 'No item name provided' };

    const itemType = this.bot.registry.itemsByName[itemName];
    if (!itemType) return { ok: false, error: `Unknown item: ${itemName}` };

    const emptySlot = this.bot.inventory.slots.findIndex((s, i) => i >= 9 && i <= 44 && !s);
    if (emptySlot === -1) return { ok: false, error: 'No empty inventory slot available' };

    // eslint-disable-next-line global-require
    const Item = require('prismarine-item')(this.bot.registry);
    const item = new Item(itemType.id, count);
    try {
      await this.bot.creative.setInventorySlot(emptySlot, item);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: `Server rejected creative item grant: ${err.message}` };
    }
  }

  async actionFollowPlayer(action) {
    const playerEntity = this.findPlayerEntity(action.playerUsername);
    if (!playerEntity) return { ok: false, error: 'Player entity not found nearby' };
    return this.actionMove({
      pos: { x: playerEntity.position.x, y: playerEntity.position.y, z: playerEntity.position.z }
    });
  }

  findPlayerEntity(username) {
    const entities = Object.values(this.bot.entities).filter(e => e.type === 'player' && e.username !== this.bot.username);
    if (username) return entities.find(e => e.username === username) || entities[0];
    return entities[0];
  }

  actionStop() {
    this.cancel();
    return { ok: true };
  }

  actionChat(action) {
    const { message } = action;
    if (!message) return { ok: false, error: 'No message provided' };
    this.bot.chat(String(message).slice(0, 256));
    return { ok: true };
  }

  getState() {
    if (!this.bot?.entity) return null;
    return {
      pos: { x: this.bot.entity.position.x, y: this.bot.entity.position.y, z: this.bot.entity.position.z },
      yaw: this.bot.entity.yaw,
      pitch: this.bot.entity.pitch,
      gameMode: this.bot.game?.gameMode,
      mode: this.mode,
      health: this.bot.health,
      food: this.bot.food,
      inventory: this.inventory,
      currentTask: this.currentTask
    };
  }
}

export default AICharacter;
